import { SplitLayout } from '@components/layout';
import { InvertedRadio, InvertedRadioChecked } from '@components/utility/radios/InvertedRadio';
import { ROLE_TO_NAME_MAP } from '@constants/account';
import { GUEST_ACCESS_HASH_KEY } from '@event/constants';
import { usePublicEventContext } from '@event/contexts/PublicEventContext';
import { Alert, Avatar, Button, Radio, Typography } from '@mui/material';
import { stringAvatar } from '@utils/common';
import clsx from 'clsx';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

enum AccountTypes {
    CURRENT = 1,
    INVITED = 2,
}

const CredentialSelect = ({ setShowGuestAccessBanner }: { setShowGuestAccessBanner: (state: boolean) => void }) => {
    const [selectedAccount, setSelectedAccount] = useState<AccountTypes | null>(null);
    const {
        guestUserInfo: { data: guestUserInfo },
        user: { data: user },
    } = usePublicEventContext();
    const [_, setSearchParams] = useSearchParams();
    const intl = useIntl();

    const handleSubmit = () => {
        if (selectedAccount === AccountTypes.CURRENT) {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                params.delete(GUEST_ACCESS_HASH_KEY);
                return params;
            });
        } else {
            setShowGuestAccessBanner(true);
        }
    };

    return (
        <SplitLayout>
            <img className="w-44 mb-6 md:mb-16 md:w-60" src="/static/img/logo.png" alt="" />
            <div className="mb-8 md:mb-16">
                <Typography className="text-[26px] font-museo mb-6 md:text-[40px]" fontWeight={600}>
                    <FormattedMessage
                        id="event.credentialSelect.title"
                        defaultMessage="Whoops! It seems you're already <blue>logged in</blue>"
                        values={{ blue: (chunks) => <span className="text-primary-600">{chunks}</span> }}
                    />
                </Typography>
                <Typography className="font-normal" variant="body1">
                    <FormattedMessage
                        id="event.credentialSelect.subtitle"
                        defaultMessage="Before you can proceed to the event please choose with what credentials you wish the view the event."
                    />
                </Typography>
            </div>
            <div className="flex flex-col gap-1 mb-8">
                <Typography className="font-normal" variant="body1">
                    <FormattedMessage id="common.currentUser" defaultMessage="Current logged in user" />
                </Typography>
                <label
                    className={clsx(
                        'relative block w-full cursor-pointer border-2 py-8 pl-6 pr-4 bg-neutral-100/50 rounded-md',
                        {
                            'border-primary-600': selectedAccount === AccountTypes.CURRENT,
                            'border-transparent': selectedAccount !== AccountTypes.CURRENT,
                        }
                    )}
                >
                    <div className="flex justify-between h-full">
                        <div className="flex max-h-8 gap-3">
                            <Avatar
                                {...stringAvatar(
                                    `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim(),
                                    {},
                                    'primary-600.main'
                                )}
                                variant="square"
                                className="size-8 rounded-sm"
                            />
                            <div className="flex flex-col">
                                <Typography className="font-semibold leading-4">{user?.email ?? ''}</Typography>
                                <Typography className="font-semibold text-xs text-neutral-800">
                                    {(user?.role && ROLE_TO_NAME_MAP(intl).get(user.role)?.toUpperCase()) ?? ''}
                                </Typography>
                            </div>
                        </div>
                        <Radio
                            name="user_account"
                            className="z-20"
                            icon={<InvertedRadio />}
                            checkedIcon={<InvertedRadioChecked />}
                            checked={selectedAccount === AccountTypes.CURRENT}
                            onChange={() => setSelectedAccount(AccountTypes.CURRENT)}
                        />
                    </div>
                </label>

                {selectedAccount === AccountTypes.CURRENT && (
                    <Alert className="rounded-md" severity="info">
                        <FormattedMessage
                            id="event.credentialSelect.infoAlert"
                            defaultMessage="This email might not have access due to event restrictions."
                        />
                    </Alert>
                )}
            </div>

            <div className="flex flex-col gap-1 pb-8">
                <Typography className="font-normal" variant="body1">
                    <FormattedMessage id="event.credentialSelect.invitedUser" defaultMessage="Invited user" />
                </Typography>
                <label
                    className={clsx(
                        'relative block w-full cursor-pointer border-2 py-8 pl-6 pr-4 bg-neutral-100/50 rounded-md',
                        {
                            'border-primary-600': selectedAccount === AccountTypes.INVITED,
                            'border-transparent': selectedAccount !== AccountTypes.INVITED,
                        }
                    )}
                >
                    <div className="flex justify-between h-full">
                        <div className="flex max-h-8 gap-3">
                            <Avatar
                                {...stringAvatar(guestUserInfo?.full_name ?? '', {}, 'primary-600.main')}
                                variant="square"
                                className="size-8 rounded-sm"
                            />
                            <div className="flex flex-col">
                                <Typography className="font-semibold leading-4">
                                    {guestUserInfo?.email ?? ''}
                                </Typography>
                                <Typography className="font-semibold text-xs text-neutral-800">
                                    <FormattedMessage
                                        id="event.credentialSelect.invitationEmail"
                                        defaultMessage="INVITATION EMAIL"
                                    />
                                </Typography>
                            </div>
                        </div>
                        <Radio
                            name="user_account"
                            className="z-20"
                            icon={<InvertedRadio />}
                            checkedIcon={<InvertedRadioChecked />}
                            checked={selectedAccount === AccountTypes.INVITED}
                            onChange={() => setSelectedAccount(AccountTypes.INVITED)}
                        />
                    </div>
                </label>
            </div>
            <Button
                className="rounded-full w-full"
                variant="contained"
                disabled={!selectedAccount}
                onClick={handleSubmit}
            >
                <FormattedMessage id="event.credentialSelect.proceedButton" defaultMessage="Proceed to event" />
            </Button>
        </SplitLayout>
    );
};

export { CredentialSelect };
